import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatTooltipModule } from '@angular/material/tooltip'
import { V2TranslationService } from '@app/v2/services/translation.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TranslateService } from '@ngx-translate/core'
import { ContentChange, QuillEditorComponent, QuillModules, QuillToolbarConfig } from 'ngx-quill'
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html'

const toolbarOptions: QuillToolbarConfig = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }, { size: ['small', false, 'large'] }],
  [{ align: [] }],
  ['clean'],
  ['link', 'video', 'image']
]

const toolbarTooltips: Record<string, string> = {
  '.ql-bold': 'Bold',
  '.ql-italic': 'Italic',
  '.ql-underline': 'Underline',
  '.ql-strike': 'Strikethrough',
  '.ql-blockquote': 'Blockquote',
  ".ql-list[value='ordered']": 'Numbered List',
  ".ql-list[value='bullet']": 'Bullet List',
  ".ql-indent[value='-1']": 'Left Indentation',
  ".ql-indent[value='+1']": 'Right Indentation',
  '.ql-direction': 'Direction',
  '.ql-header': 'Header',
  '.ql-color': 'Text Color',
  '.ql-background': 'Background Color',
  '.ql-size': 'Text Size',
  '.ql-align': 'Align',
  '.ql-clean': 'Remove Formatting',
  '.ql-link': 'Hyperlink',
  '.ql-video': 'Insert Video',
  '.ql-image': 'Insert Image'
}

@Component({
  selector: 'ngx-editor-tooltip',
  template: `
    <div class="mat-mdc-tooltip mdc-tooltip mdc-tooltip--shown">
      {{ message }}
    </div>
  `,
  styles: [
    `
      .mat-mdc-tooltip {
        background-color: #424242;
        color: white;
        padding: 6px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.12);
        z-index: 1000;
        max-width: 200px;
        text-align: center;
        word-wrap: break-word;
      }
    `
  ],
  standalone: true
})
export class EditorTooltipComponent {
  @Input() message: string
}

/**
 * EditorComponent is a customizable rich text editor built using ngx-quill.
 * It allows users to input and format text with various options and emits
 * changes to the parent component.
 *
 * @Inputs
 * @Input message - The initial value of the editor.
 * @Input height - The height of the editor in pixels (optional, default: 265).
 * @Input showMenuBar - Whether to show the menu bar (optional, default: true).
 * @Input border - Whether to show the border (optional, default: true).
 * @Input editorPadding - Whether to add padding to the editor (optional, default: false).
 * @Input placeholder - The placeholder text for the editor (optional, default: '').
 * @Input whiteBackground - Whether to show a white background (optional, default: true).
 * @Input containerHeight - The height of the editor container in pixels (optional, default: 130).
 * @Input blackPlaceholder - Whether to show a black placeholder (optional, default: false).
 * @Input invitationInfo - The invitation info object (optional, default: null).
 * @Input borderRadius - The border radius of the editor (optional, default: '12px').
 * @Input maxCharacterLimit - The maximum character limit for the editor (optional, default: 0).
 * @Input lineHeight - The line height of the editor content (optional, default: '1.2em').
 *
 * @Outputs
 * @event valueChange - Emits the current value of the editor when it changes.
 * @event blur - Emits when the editor loses focus.
 */
@Component({
  selector: 'ngx-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  standalone: true,
  imports: [CommonModule, QuillEditorComponent, FormsModule, MatTooltipModule]
})
export class EditorComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() message = ''
  @Input() height?: number = 265
  @Input() showMenuBar?: boolean = true
  @Input() border?: boolean = true
  @Input() editorPadding?: boolean = false
  @Input() placeholder?: string = ''
  @Input() whiteBackground?: boolean = true
  @Input() containerHeight?: number = 130
  @Input() blackPlaceholder?: boolean = false
  @Input() invitationInfo?: { message?: string }
  @Input() borderRadius?: string = '12px'
  @Input() maxCharacterLimit?: number = 0
  @Input() enableFloatingToolbar?: boolean = false
  @Input() lineHeight?: string = '1.2em'

  @Output() valueChange: EventEmitter<string> = new EventEmitter<string>()
  @Output() blurred: EventEmitter<string> = new EventEmitter<string>()
  @Output() characterCountChange: EventEmitter<number> = new EventEmitter<number>()

  public imageURL = ''
  public showImageInput = false
  public imageInputPositionLeft = 0
  public imageInputPositionTop = 0
  public editorModules: QuillModules
  public showSizeModal = false
  public selectedImageWidth = ''
  public selectedImageHeight = ''
  public initialMessage = ''
  public showFloatingToolbar = false
  public floatingToolbarTop = 0
  public floatingToolbarLeft = 0

  private _EditorSelectionIndex = 0
  private _CurrentImageElement: HTMLImageElement | null = null
  private _tooltipOverlays: Map<HTMLElement, OverlayRef> = new Map()
  private _floatingToolbarTimeout: any = null
  private _isSelectInteracting = false
  private _eventListeners: Array<{ element: EventTarget; event: string; handler: EventListener }> = []
  private _isUserTyping = false
  private _isProgrammaticUpdate = false
  private _isScrollLocked = false
  private _lockedScrollPosition = { top: 0, left: 0 }
  private _scrollPreventHandlers: EventListener[] = []
  private _lastFocusedElement: HTMLElement | null = null

  @ViewChild('editor') editor: QuillEditorComponent

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private injector: Injector,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private cdr: ChangeDetectorRef,
    private translationService: V2TranslationService,
    private _alertService: AlertService,
    private _translateService: TranslateService
  ) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    setTimeout(() => {
      if (this.el.nativeElement.querySelector('.image-url-modal')?.contains(event.target)) return
      if (this.el.nativeElement.querySelector('div[class*="tw-absolute"]')?.contains(event.target) || this._isSelectInteracting) return
      if (this.showImageInput && !(event.target as HTMLElement).closest('.ql-image') && !(event.target as HTMLElement).closest('.image-url')) {
        this.showImageInput = false
      }
      this._onDocumentClick(event)
    })
  }

  ngOnInit() {
    this.editorModules = {
      toolbar: {
        container: toolbarOptions,
        handlers: {
          image: this._ImageHandler.bind(this)
        }
      },
      history: {
        delay: 1000,
        maxStack: 500,
        userOnly: true
      }
    }
    this.initialMessage = this.message || this.invitationInfo?.message
  }

  ngAfterViewInit() {
    document.addEventListener('click', this._onDocumentClick.bind(this))
    // Add manual blur detection
    this._addTrackedEventListener(document, 'click', () => this.cleanMessage())
    // Initialize tooltips after view is ready
    setTimeout(() => {
      this._ConfigureEditor()
      this.cdr.detectChanges()
    })
  }

  ngOnDestroy() {
    // Clean up all tracked event listeners
    this._eventListeners.forEach(({ element, event, handler }) => element.removeEventListener(event, handler))
    this._eventListeners = []

    // Clean up all tooltip overlays
    this._tooltipOverlays.forEach((overlayRef) => {
      if (overlayRef) {
        overlayRef.dispose()
      }
    })
    this._tooltipOverlays.clear()

    if (this._floatingToolbarTimeout) clearTimeout(this._floatingToolbarTimeout)

    // Ensure scroll is unlocked on destroy
    this._unlockScroll()
  }

  /**
   * @description Helper method to add and track event listeners for memory leak prevention
   */
  private _addTrackedEventListener(element: EventTarget, event: string, handler: EventListener) {
    element.addEventListener(event, handler)
    this._eventListeners.push({ element, event, handler })
  }

  /**
   * @description Lock scroll to prevent scrolling while text is selected
   */
  private _lockScroll() {
    if (this._isScrollLocked) return

    // Store current scroll position IMMEDIATELY
    this._lockedScrollPosition = {
      top: window.pageYOffset || document.documentElement.scrollTop,
      left: window.pageXOffset || document.documentElement.scrollLeft
    }

    this._isScrollLocked = true

    // Create scroll prevention handlers with immediate effect
    const preventScroll = (e: Event) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      // Force scroll position back immediately
      window.scrollTo(this._lockedScrollPosition.left, this._lockedScrollPosition.top)
      return false
    }

    const preventWheel = (e: WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      return false
    }

    const preventTouchMove = (e: TouchEvent) => {
      // Allow touch move inside the editor only
      if (this.el.nativeElement.contains(e.target as Node)) {
        return true
      }
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      return false
    }

    const preventKeyScroll = (e: KeyboardEvent) => {
      // Prevent keyboard scrolling (Page Up/Down, Arrow keys, Space, etc.)
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Space']
      if (scrollKeys.includes(e.key) && !this.el.nativeElement.contains(e.target as Node)) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        return false
      }
      return true
    }

    // Store handlers for cleanup
    this._scrollPreventHandlers = [preventScroll, preventWheel, preventTouchMove, preventKeyScroll]

    // Add event listeners with immediate effect
    window.addEventListener('scroll', preventScroll, { passive: false, capture: true })
    window.addEventListener('wheel', preventWheel, { passive: false, capture: true })
    window.addEventListener('touchmove', preventTouchMove, { passive: false, capture: true })
    window.addEventListener('keydown', preventKeyScroll, { passive: false, capture: true })
    document.addEventListener('scroll', preventScroll, { passive: false, capture: true })

    // Also add CSS-based prevention as backup
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
  }

  /**
   * @description Unlock scroll to allow normal scrolling
   */
  private _unlockScroll() {
    if (!this._isScrollLocked) return

    this._isScrollLocked = false

    // Remove all scroll prevention listeners
    if (this._scrollPreventHandlers.length > 0) {
      const [preventScroll, preventWheel, preventTouchMove, preventKeyScroll] = this._scrollPreventHandlers

      // Remove with same options as they were added
      window.removeEventListener('scroll', preventScroll, true)
      window.removeEventListener('wheel', preventWheel, true)
      window.removeEventListener('touchmove', preventTouchMove, true)
      window.removeEventListener('keydown', preventKeyScroll, true)
      document.removeEventListener('scroll', preventScroll, true)

      // Clear handlers array
      this._scrollPreventHandlers = []
    }

    // Restore CSS overflow immediately
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    // Small delay to ensure all events are processed cleanly
    setTimeout(() => {
      // Scroll is now fully unlocked and should work normally
    }, 10)
  }

  /**
   * @description Handle document click event
   * @param event MouseEvent
   */
  private _onDocumentClick(event: MouseEvent): void {
    if (!this.editor?.quillEditor) return
    const clickedInside = this.el.nativeElement.contains(event.target)
    if (clickedInside) return

    if (this._isSelectInteracting) return

    const target = event.target as HTMLElement
    if (target.tagName === 'OPTION' || target.tagName === 'SELECT' || target.closest('select')) return

    this._hideFloatingToolbar()
    this._unlockScroll()

    if (this.invitationInfo) this.invitationInfo.message = this._getInlineStyledContent()
  }

  /**
   * ngx-quill only runs the text-change pipeline when onContentChanged is observed OR ngModelChange should fire.
   * Subscribing here ensures user edits always sync to invitationInfo (invite_message payload).
   */
  onQuillContentChanged(event: ContentChange): void {
    if (!this.invitationInfo || !event.editor) return
    this.invitationInfo.message = this._getInlineStyledContent(event.editor)
  }

  /**
   * Copies the current Quill HTML into invitationInfo.message immediately before PATCH (invite-bulk / invite-test).
   * Parent views cannot reliably query ngx-editor across nested templates; they call this via template wrappers.
   */
  flushInvitationMessageToModel(): void {
    if (!this.invitationInfo || !this.editor?.quillEditor) return
    this.invitationInfo.message = this._getInlineStyledContent()
  }

  /**
   * @description Toggle the image input visibility
   */
  private _ImageHandler() {
    this._EditorSelectionIndex = this.editor.quillEditor.getSelection()?.index ?? 0
    const bounds = this.editor.quillEditor.getBounds(this._EditorSelectionIndex)
    this.imageInputPositionLeft = bounds.left * 1.1
    this.imageInputPositionTop = bounds.top * 1.1
    this.showImageInput = !this.showImageInput
    if (this.showImageInput) setTimeout(() => (document.querySelector('#image-url') as HTMLInputElement).focus())
  }

  /**
   * @description Get the actual text length excluding trailing newline
   * Quill's getText() always includes a trailing \n, so we subtract 1 to get the actual content length
   */
  private _getActualTextLength(): number {
    if (!this.editor?.quillEditor) return 0
    const text = this.editor.quillEditor.getText()
    // Remove trailing newline if it exists
    return text.endsWith('\n') ? text.length - 1 : text.length
  }

  /**
   * @description Add character limit handler to prevent typing when limit is reached
   */
  private _addCharacterLimitHandler(): void {
    if (!this.editor?.quillEditor || !this.maxCharacterLimit) return

    const quillEditor = this.editor.quillEditor

    // Add keydown event listener to prevent typing when limit is reached
    this._addTrackedEventListener(quillEditor.root, 'keydown', (event: KeyboardEvent) => {
      const currentLength = this._getActualTextLength()

      // Allow backspace, delete, arrow keys, and other non-character keys
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'PageUp',
        'PageDown',
        'Tab',
        'Enter',
        'Escape',
        'F1',
        'F2',
        'F3',
        'F4',
        'F5',
        'F6',
        'F7',
        'F8',
        'F9',
        'F10',
        'F11',
        'F12'
      ]

      // Allow Ctrl/Cmd combinations (for shortcuts like Ctrl+A, Ctrl+C, etc.)
      if (event.ctrlKey || event.metaKey) {
        return true
      }

      // If this key would add a printable character and would push us over the limit, prevent it
      if (!allowedKeys.includes(event.key)) {
        const isCharacterKey = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey

        if (isCharacterKey) {
          const selection = quillEditor.getSelection()
          const selectionLength = selection?.length ?? 0

          // Prospective length after this key press: remove selected chars, add 1 new char
          const prospectiveLength = currentLength - selectionLength + 1

          if (prospectiveLength > this.maxCharacterLimit) {
            event.preventDefault()
            event.stopPropagation()
            this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.orgSettings.publicInfo.contentSizeLimitExceeded'))
            return false
          }
        }
      }

      return true
    })

    // Add text-change event listener to handle any content that exceeds the limit
    quillEditor.on('text-change', (delta: any, oldDelta: any, source: string) => {
      const currentLength = this._getActualTextLength()

      // Always emit the latest character count for consumers (so they match editor's own limit logic)
      this.characterCountChange.emit(currentLength)

      if (currentLength > this.maxCharacterLimit) {
        // If we're over the limit, truncate the content to the maximum allowed
        const currentText = quillEditor.getText()
        const textWithoutNewline = currentText.endsWith('\n') ? currentText.slice(0, -1) : currentText
        const truncatedText = textWithoutNewline.substring(0, this.maxCharacterLimit) + '\n'

        // Replace the content with truncated version
        quillEditor.setText(truncatedText)

        // Set cursor to the end
        quillEditor.setSelection(truncatedText.length, 0)

        // After truncation, recompute and emit the corrected length
        const truncatedLength = this._getActualTextLength()
        this.characterCountChange.emit(truncatedLength)

        // Scroll to the end
        setTimeout(() => {
          this._scrollToEnd()
        }, 10)
      }
    })

    // Also handle paste events
    this._addTrackedEventListener(quillEditor.root, 'paste', (event: ClipboardEvent) => {
      const currentLength = this._getActualTextLength()
      const pastedText = event.clipboardData?.getData('text/plain') || ''

      const willExceedLimit = currentLength + pastedText.length > this.maxCharacterLimit

      if (willExceedLimit) {
        // Calculate how many characters we can paste
        const availableSpace = this.maxCharacterLimit - currentLength

        if (availableSpace <= 0) {
          // No space available, prevent paste
          event.preventDefault()
          event.stopPropagation()
          this._alertService.alert(Alert.ERROR, this._translateService.instant('v2.orgSettings.publicInfo.contentSizeLimitExceeded'))
          return false
        }

        // Cut the pasted text to fit the available space
        const truncatedText = pastedText.substring(0, availableSpace)

        // Prevent the default paste behavior
        event.preventDefault()
        event.stopPropagation()

        // Manually insert the truncated text
        const selection = quillEditor.getSelection()
        if (selection) {
          quillEditor.insertText(selection.index, truncatedText)

          // Set cursor to the end of the inserted text
          const newIndex = selection.index + truncatedText.length
          quillEditor.setSelection(newIndex, 0)

          setTimeout(() => {
            this._scrollToEnd()
          }, 10)
        }

        return false
      }

      return true
    })
  }

  /**
   * @description Called when the Quill editor is fully created and ready
   * @param quill - The Quill editor instance
   */
  public onEditorCreated(quill: any) {
    this._OverrideQuillStyles()
    this._ConfigureEditor()
    this._addCharacterLimitHandler()
    this.cdr.detectChanges()
  }

  /**
   * @description Override quill styles according to the menu bar visibility and language direction
   */
  private _OverrideQuillStyles() {
    const quillEditor = this.editor.quillEditor.root
    this.border = !this.showMenuBar && this.border ? false : this.border

    // Add or remove the editor-headerless class based on showMenuBar
    if (!this.showMenuBar) quillEditor.classList.add('editor-headerless')
    else quillEditor.classList.remove('editor-headerless')

    // Set direction based on current language from translation service
    const currentLang = this.translationService.currentLang()
    if (currentLang?.direction === 'rtl') {
      quillEditor.style.direction = 'rtl'
      quillEditor.style.textAlign = 'right'
    } else {
      quillEditor.style.direction = 'ltr'
      quillEditor.style.textAlign = 'left'
    }

    quillEditor.style.setProperty('--editor-height', `${this.height}px`)
    quillEditor.style.setProperty('--editor-border', this.border ? null : 'none')
    quillEditor.style.setProperty('--editor-border-radius', this.borderRadius)
    quillEditor.style.setProperty('--editor-line-height', this.lineHeight)
  }

  /**
   * @description Initialize image resize functionality and tooltips
   */
  private _ConfigureEditor() {
    // Check if editor and quillEditor are available
    if (!this.editor || !this.editor.quillEditor || !this.editor.quillEditor.root) {
      return
    }

    const quillEditor = this.editor.quillEditor.root

    // For each .ql-formats container
    const formatsContainers = this.el.nativeElement.querySelectorAll('.ql-toolbar .ql-formats')
    formatsContainers.forEach((container: HTMLElement) => {
      // For each direct child (button, select, span, etc.)
      Array.from(container.children).forEach((el: HTMLElement) => {
        // Find the matching tooltip text based on selector
        let tooltipText = ''
        for (const [selector, text] of Object.entries(toolbarTooltips)) {
          if (el.matches(selector)) {
            tooltipText = text
            break
          }
        }
        if (tooltipText) {
          el.removeAttribute('title')
          this._createMaterialTooltip(el, tooltipText)
        }
      })
    })

    // Image selection handlers
    this._addTrackedEventListener(quillEditor, 'click', (event: MouseEvent) => {
      const target = event.target as HTMLImageElement
      target.tagName.toLowerCase() === 'img' ? this._ShowImageSelection(target) : this._HideImageSelection()
    })

    this._addTrackedEventListener(quillEditor, 'contextmenu', (event: MouseEvent) => {
      event.preventDefault()
      this._OpenResizeModal(event)
    })

    this._addTrackedEventListener(quillEditor, 'dblclick', (event: MouseEvent) => {
      event.stopPropagation()
      this._OpenResizeModal(event)
    })

    // Floating toolbar event listeners - only add if floating toolbar is enabled
    if (this.enableFloatingToolbar) {
      this._addTrackedEventListener(quillEditor, 'selection-change', (range: any) => this._handleSelection(range))

      this._addTrackedEventListener(quillEditor, 'mouseup', (event: MouseEvent) => {
        const target = event.target as HTMLElement
        if (target.tagName === 'SELECT' || target.closest('select')) return
        setTimeout(() => this._handleSelection())
      })

      this._addTrackedEventListener(quillEditor, 'mouseover', (event: MouseEvent) => this._handleSelection(undefined, event))

      this._addTrackedEventListener(quillEditor, 'mouseleave', () => setTimeout(() => this._handleSelection(), 10))

      // Window event listeners for toolbar repositioning - only add if floating toolbar is enabled
      this._addTrackedEventListener(window, 'scroll', (event: Event) => {
        if (this.showFloatingToolbar) {
          // Check if scroll is happening outside the editor
          const target = event.target as Element
          const isEditorScroll = target === quillEditor || quillEditor.contains(target)

          // Hide toolbar when scrolling outside the editor
          if (!isEditorScroll) this._hideFloatingToolbar()
          else {
            // Reposition toolbar when scrolling inside the editor
            clearTimeout(this._floatingToolbarTimeout)
            this._floatingToolbarTimeout = setTimeout(() => this._repositionToolbar(), 50)
          }
        }
      })

      this._addTrackedEventListener(window, 'resize', () => {
        if (this.showFloatingToolbar) {
          clearTimeout(this._floatingToolbarTimeout)
          this._floatingToolbarTimeout = setTimeout(() => this._repositionToolbar(), 100)
        }
      })
    }
  }

  /**
   * @description Create a Material Design tooltip for a given element
   * @param element - The DOM element to attach the tooltip to
   * @param message - The tooltip message
   */
  private _createMaterialTooltip(element: HTMLElement, message: string) {
    let overlayRef: OverlayRef | null = null
    let isTooltipVisible = false

    const showTooltip = () => {
      if (isTooltipVisible || !message) return

      const positionStrategy = this.overlayPositionBuilder.flexibleConnectedTo(element).withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8
        },
        {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8
        }
      ])

      overlayRef = this.overlay.create({
        positionStrategy,
        panelClass: 'mat-mdc-tooltip-panel-custom',
        scrollStrategy: this.overlay.scrollStrategies.close()
      })

      const tooltipPortal = new ComponentPortal(EditorTooltipComponent, null, this.injector)
      const tooltipRef = overlayRef.attach(tooltipPortal)
      tooltipRef.instance.message = message

      isTooltipVisible = true
      this._tooltipOverlays.set(element, overlayRef)
    }

    const hideTooltip = () => {
      if (overlayRef && isTooltipVisible) {
        overlayRef.dispose()
        overlayRef = null
        isTooltipVisible = false
        this._tooltipOverlays.delete(element)
      }
    }

    // Add event listeners for hover
    this.renderer.listen(element, 'mouseenter', showTooltip)
    this.renderer.listen(element, 'mouseleave', hideTooltip)
    this.renderer.listen(element, 'click', hideTooltip)
  }

  /**
   * @description Open the image resize modal
   * @param event MouseEvent
   */
  private _OpenResizeModal(event: MouseEvent) {
    const target = event.target as HTMLImageElement
    if (target.tagName.toLowerCase() !== 'img') {
      this._CurrentImageElement = null
      return
    }
    this._CurrentImageElement = target
    this.showSizeModal = true
    this.selectedImageWidth = `${(target.width + 2).toString()}px` || '100%'
    this.selectedImageHeight = `${(target.height + 2).toString()}px` || '100%'
  }

  /**
   * @description Check the character limit, update the message value and emit the value
   * @param value string
   */
  public updateValue(value: string) {
    if (!this.editor?.quillEditor) {
      if (this.invitationInfo) this.invitationInfo.message = value ?? ''
      this.valueChange.emit(value ?? '')
      return
    }
    this._isUserTyping = true
    const currentCharacterCount = this._getActualTextLength()

    // Keep parent components in sync with the editor's internal character counting
    this.characterCountChange.emit(currentCharacterCount)

    const parsedMessage = this._getInlineStyledContent()
    if (this.invitationInfo) this.invitationInfo.message = parsedMessage
    this.valueChange.emit(parsedMessage)
  }

  /**
   * @description Update message value and remove non-printable characters on blur
   */
  public cleanMessage() {
    const parsedMessage = this._getInlineStyledContent()
    if (this.invitationInfo) this.invitationInfo.message = parsedMessage
    this.blurred.emit(parsedMessage)
  }

  /**
   * @description Add image to the editor
   * @param event Event
   */
  public addImageToEditor(event: Event) {
    event.preventDefault()
    setTimeout(() => {
      this.editor.quillEditor.insertEmbed(this._EditorSelectionIndex ?? 0, 'image', this.imageURL)
      this.imageURL = ''
      this.showImageInput = false
      this.editor.quillEditor.setSelection(++this._EditorSelectionIndex, 0)
      this.editor.quillEditor.focus()
      this.updateValue(this._getInlineStyledContent())
    })
  }

  /**
   * @description Show the selected image with a border
   * @param image HTMLElement
   */
  private _ShowImageSelection(image: HTMLImageElement) {
    this._CurrentImageElement = image
    this._CurrentImageElement.style.border = '1px dashed blue'
  }

  /**
   * @description Hide the selected image border
   */
  private _HideImageSelection() {
    if (!this._CurrentImageElement) return
    this._CurrentImageElement.style.border = ''
    this._CurrentImageElement = null
  }

  /**
   * @description Resize the selected image
   */
  public applyNewImageDimensions() {
    if (!this._CurrentImageElement) return
    //append px if not present
    const width = !this.selectedImageWidth.match(/(^\d+%$|^\d+px$)/m)?.[0] ? `${this.selectedImageWidth}px` : this.selectedImageWidth
    const height = !this.selectedImageHeight.match(/(^\d+%$|^\d+px$)/m)?.[0] ? `${this.selectedImageHeight}px` : this.selectedImageHeight
    this._CurrentImageElement.style.setProperty('width', width)
    this._CurrentImageElement.style.setProperty('height', height)
    this._getInlineStyledContent()
    this.showSizeModal = false
    this._HideImageSelection()
  }

  /**
   * @description Close the image dimensions modal
   */
  public closeImageDimensionsModal() {
    this.showSizeModal = false
    this._CurrentImageElement = null
  }

  /**
   * @description Get the inline styled content
   * @returns string
   * @memberof EditorComponent
   * */
  private _getInlineStyledContent(quillInstance?: ContentChange['editor'] | null) {
    const quill = quillInstance ?? this.editor?.quillEditor
    if (!quill?.root || (!quill.getText().trim() && !quill.root.querySelector('img, video, iframe'))) return ''

    quill.root.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      const width = img.style.width || img.getAttribute('width') || '100%'
      const height = img.style.height || img.getAttribute('height') || '100%'
      img.style.width = width
      img.style.height = height
      img.setAttribute('width', width)
      img.setAttribute('height', height)
    })
    const delta = quill.getContents()
    const converter = new QuillDeltaToHtmlConverter(delta.ops || [], { inlineStyles: true })
    const htmlWithInlineStyles = converter.convert()

    // Normalize empty HTML content (e.g., <p></p>, <p><br></p>) to empty string
    return this._normalizeEmptyContent(htmlWithInlineStyles)
  }

  /**
   * @description Normalize empty HTML content to empty string
   * Checks if HTML contains any meaningful text content
   * @param html - The HTML string to normalize
   * @returns string - Empty string if no content, otherwise original HTML
   */
  private _normalizeEmptyContent(html: string): string {
    if (!html || html.trim() === '') return ''

    // Extract text content from HTML
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    const innerHTML = tmp.innerHTML || ''
    const trimmedText = innerHTML?.trim() || ''

    if (trimmedText === '' || trimmedText === '\n' || trimmedText === '\r\n') return ''
    return html
  }

  /**
   * @description Unified selection handler - consolidates all selection logic
   * @param range - Optional selection range, if not provided will get current selection
   * @param event - Optional mouse event for special handling
   */
  private _handleSelection(range?: any, event?: MouseEvent) {
    if (!this.enableFloatingToolbar || !this.editor?.quillEditor) return

    // Special handling for image clicks
    if (event) {
      const target = event.target as HTMLElement
      if (target.tagName.toLowerCase() === 'img') {
        this._hideFloatingToolbar()
        this._unlockScroll()
        return
      }
    }

    // Clear any existing timeout
    if (this._floatingToolbarTimeout) clearTimeout(this._floatingToolbarTimeout)

    // Get current selection if no range provided
    const selection = range || this.editor.quillEditor.getSelection()

    if (selection && selection.length > 0) {
      // IMMEDIATELY lock scroll for ANY text selection to prevent glitches
      this._lockScroll()

      // For very large selections, use a much longer delay
      const isVeryLargeSelection = selection.length > 500
      const delay = isVeryLargeSelection ? 300 : 50 // Even longer delay for large selections

      this._floatingToolbarTimeout = setTimeout(() => {
        this._showFloatingToolbar(selection)
        this.cdr.detectChanges()
      }, delay)
    } else {
      this._hideFloatingToolbar()
      this._unlockScroll()
    }
  }

  /**
   * @description Show the floating toolbar at the specified position
   * @param range - The selection range
   */
  private _showFloatingToolbar(range: any) {
    if (!this.editor.quillEditor || !this.enableFloatingToolbar) return

    // For very large selections, use a much more conservative approach
    const isVeryLargeSelection = range.length > 500

    if (isVeryLargeSelection) {
      // For large selections, avoid ALL bounds calculations - just use fixed positioning
      const editorRect = this.editor.quillEditor.root.getBoundingClientRect()
      const wrapperRect = this.el.nativeElement.getBoundingClientRect()

      // Position toolbar at a fixed location above the editor
      this.floatingToolbarTop = -67 // Fixed position above editor
      this.floatingToolbarLeft = editorRect.left - wrapperRect.left // Left-aligned with editor
      this.showFloatingToolbar = true
      this.cdr.detectChanges()
    } else {
      // For normal selections, use the full positioning logic
      requestAnimationFrame(() => {
        try {
          const bounds = this.editor.quillEditor.getBounds(range.index, range.length)
          const editorRect = this.editor.quillEditor.root.getBoundingClientRect()
          const wrapperRect = this.el.nativeElement.getBoundingClientRect()

          // Toolbar dimensions
          const toolbarHeight = 55
          const toolbarWidth = 300
          const padding = 12

          // Calculate selection position
          const selectionTop = bounds.top + editorRect.top
          const selectionLeft = bounds.left + editorRect.left
          const selectionBottom = selectionTop + bounds.height

          // Calculate toolbar left position to center it above the selected text
          const selectionWidth = Math.max(bounds.width, 20)
          const selectionCenter = selectionLeft - wrapperRect.left + selectionWidth / 2
          const toolbarCenter = toolbarWidth / 2
          let toolbarLeft = selectionCenter - toolbarCenter

          // Ensure toolbar stays within editor bounds horizontally
          const editorLeft = editorRect.left - wrapperRect.left
          const editorRight = editorRect.right - wrapperRect.left
          const minLeft = editorLeft
          const maxLeft = editorRight - toolbarWidth

          toolbarLeft = Math.max(minLeft, Math.min(toolbarLeft, maxLeft))

          // Calculate toolbar top position above the selected line
          let toolbarTop = selectionTop - wrapperRect.top - toolbarHeight - padding

          // Get viewport dimensions
          const viewportHeight = window.innerHeight
          const scrollTop = this._lockedScrollPosition.top

          // Check if there's enough space above the selection in viewport
          const selectionTopInViewport = selectionTop - scrollTop
          const selectionBottomInViewport = selectionBottom - scrollTop

          // If not enough space above in viewport, position below the selection
          if (selectionTopInViewport < toolbarHeight + padding && selectionBottomInViewport + toolbarHeight + padding < viewportHeight) {
            toolbarTop = selectionBottom - wrapperRect.top + padding
          }

          // Allow toolbar to go outside editor boundaries to prevent overlap
          const maxTopOffset = -(toolbarHeight + 20)
          const wrapperHeight = wrapperRect.height
          const maxBottomOffset = wrapperHeight + 20

          toolbarTop = Math.max(maxTopOffset, Math.min(toolbarTop, maxBottomOffset))

          this.floatingToolbarTop = toolbarTop
          this.floatingToolbarLeft = toolbarLeft
          this.showFloatingToolbar = true
          this.cdr.detectChanges()
        } catch (error) {
          this._hideFloatingToolbar()
        }
      })
    }

    // Add event listeners for select elements after toolbar is rendered
    setTimeout(() => {
      this._addSelectEventListeners()
      this._addToolbarEventListeners()
    }, 10)
  }

  /**
   * @description Add event listeners for select elements in floating toolbar
   */
  private _addSelectEventListeners() {
    const selectElements = this.el.nativeElement.querySelectorAll('div[class*="tw-absolute"] select')
    selectElements.forEach((select: HTMLSelectElement) => {
      // Add tracked listeners (old ones are automatically cleaned up)
      this._addTrackedEventListener(select, 'mousedown', this._onSelectMouseDown)
      this._addTrackedEventListener(select, 'blur', this._onSelectBlur)
      this._addTrackedEventListener(select, 'change', this._onSelectChange)
      this._addTrackedEventListener(select, 'click', this._onSelectClick)
      this._addTrackedEventListener(select, 'focus', this._onSelectFocus)
    })
  }

  private _onSelectMouseDown = () => {
    this._isSelectInteracting = true
  }

  private _onSelectBlur = () => {
    this._isSelectInteracting = false
  }

  private _onSelectChange = () => {
    this._isSelectInteracting = false
  }

  private _onSelectClick = (event: Event) => {
    event.stopPropagation()
    this._isSelectInteracting = true
  }

  private _onSelectFocus = () => {
    this._isSelectInteracting = true
  }

  /**
   * @description Add event listeners to the floating toolbar container
   */
  private _addToolbarEventListeners() {
    const toolbar = this.el.nativeElement.querySelector('div[class*="tw-absolute"]')
    if (!toolbar) return

    // Prevent all events from bubbling up
    const preventBubble = (event: Event) => event.stopPropagation()

    this._addTrackedEventListener(toolbar, 'click', preventBubble)
    this._addTrackedEventListener(toolbar, 'mousedown', preventBubble)

    this._addTrackedEventListener(toolbar, 'focusin', (event: Event) => {
      event.stopPropagation()
      this._isSelectInteracting = true
    })

    this._addTrackedEventListener(toolbar, 'focusout', (event: Event) => {
      event.stopPropagation()
      setTimeout(() => {
        if (!toolbar.contains(document.activeElement)) {
          this._isSelectInteracting = false
        }
      }, 100)
    })
  }

  /**
   * @description Hide the floating toolbar
   */
  private _hideFloatingToolbar() {
    if (this._floatingToolbarTimeout) {
      clearTimeout(this._floatingToolbarTimeout)
      this._floatingToolbarTimeout = null
    }
    this.showFloatingToolbar = false
    this._unlockScroll()
    this.cdr.detectChanges()
  }

  /**
   * @description Reposition the toolbar based on current selection
   */
  private _repositionToolbar() {
    if (!this.editor.quillEditor || !this.enableFloatingToolbar) return

    const selection = this.editor.quillEditor.getSelection()
    if (selection && selection.length > 0) this._showFloatingToolbar(selection)
    else this._hideFloatingToolbar()
  }

  /**
   * @description Apply formatting using Quill's built-in toolbar API
   * @param format - The format to apply
   * @param value - Optional value for the format
   */
  public applyFormat(format: string, value?: any) {
    if (!this.editor?.quillEditor) return

    const selection = this.editor.quillEditor.getSelection()
    if (!selection || selection.length === 0) return

    // If no value is provided, toggle the format based on current state
    let formatValue = value
    if (value === undefined) {
      const currentFormat = this.editor.quillEditor.getFormat()
      formatValue = !currentFormat[format]
    }

    // Use Quill's format method directly
    this.editor.quillEditor.format(format, formatValue)
    this.editor.quillEditor.focus()

    // Manually trigger updateValue to emit the formatted content
    setTimeout(() => {
      const currentContent = this.editor.quillEditor.getText()
      this.updateValue(currentContent)
    }, 0)
  }

  /**
   * @description Check if a format is active
   * @param format - The format to check
   * @returns boolean
   */
  public isFormatActive(format: string): boolean {
    if (!this.editor || !this.editor.quillEditor) return false
    return this.editor.quillEditor.getFormat()[format] === true
  }

  /**
   * @description Toggle list style using Quill's toolbar API
   * @param listType - The list type to toggle
   */
  public toggleList(listType: string) {
    if (!this.editor?.quillEditor) return

    const selection = this.editor.quillEditor.getSelection()
    if (!selection || selection.length === 0) return

    const currentFormat = this.editor.quillEditor.getFormat()
    const isCurrentListType = currentFormat.list === listType

    // If the current list type is the same, remove the list formatting
    // Otherwise, apply the new list type
    const formatValue = isCurrentListType ? false : listType

    this.editor.quillEditor.format('list', formatValue)
    this.editor.quillEditor.focus()

    // Trigger change detection to update button states
    this.cdr.detectChanges()

    // Manually trigger updateValue to emit the formatted content
    setTimeout(() => {
      const currentContent = this.editor.quillEditor.getText()
      this.updateValue(currentContent)
    }, 0)
  }

  /**
   * @description Check if list is active
   * @param listType - The list type to check
   * @returns boolean
   */
  public isListActive(listType: string): boolean {
    if (!this.editor || !this.editor.quillEditor) return false
    return this.editor.quillEditor.getFormat().list === listType
  }

  /**
   * @description Scroll to the end of the editor content
   */
  private _scrollToEnd() {
    if (!this.editor?.quillEditor) return

    const quillEditor = this.editor.quillEditor
    const textLength = quillEditor.getText().length

    // Set selection to the end
    quillEditor.setSelection(textLength, 0)

    // Scroll the editor container to show the end
    const editorContainer = quillEditor.root
    if (editorContainer) {
      editorContainer.scrollTop = editorContainer.scrollHeight
    }
  }
}
